import { useCallback, useEffect, useState } from 'react';
import mabarService from '@/services/mabarService';
import communityService, { MabarEvent, MabarParticipant } from '@/services/communityService';
import badmintonService, {
  BadmintonSessionListItem,
  BadmintonStandingItem,
  BadmintonTopMmrItem,
} from '@/services/badmintonService';
import { flattenBadmintonGames } from '@/components/community/badminton/badmintonListUtils';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

export function useBadmintonStatistik(slug: string | undefined, isClientView: boolean) {
  const [event, setEvent] = useState<MabarEvent | null>(null);
  const [sessions, setSessions] = useState<BadmintonSessionListItem[]>([]);
  const [standings, setStandings] = useState<BadmintonStandingItem[]>([]);
  const [topMmr, setTopMmr] = useState<BadmintonTopMmrItem[]>([]);
  const [participants, setParticipants] = useState<MabarParticipant[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const loadData = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const [sessData, standData, mmrData] = await Promise.all([
        badmintonService.listSessions(slug),
        badmintonService.getStandings(slug),
        badmintonService.getTopMmr(slug),
      ]);
      if (isClientView) {
        const ev = await mabarService.getEventBySlug(slug);
        setEvent(
          ev
            ? ({
                name: ev.name,
                event_date: ev.event_date,
                start_time: ev.start_time,
                end_time: ev.end_time,
                address: ev.address,
                lokasi_arena: ev.lokasi_arena,
                host: ev.host,
                sport_category: ev.sport_category,
              } as MabarEvent)
            : null
        );
        setParticipants([]);
      } else {
        const eventData = await communityService.getEventBySlug(slug);
        setEvent(eventData);
        setParticipants([]);
      }
      setSessions(sessData ?? []);
      setStandings(standData ?? []);
      setTopMmr(mmrData ?? []);
      try {
        const access = await badmintonService.getAccess(slug);
        setCanEdit(access.canEdit);
        if (access.canEdit && !isClientView) {
          const partRes = await communityService.getEventParticipants(slug, {
            status: 'confirmed',
            limit: 200,
          });
          setParticipants(partRes.participants ?? []);
        }
      } catch {
        setCanEdit(false);
      }
      setErrorMessage('');
    } catch (err: unknown) {
      setErrorMessage(getApiErrorMessage(err, 'Gagal memuat data'));
    } finally {
      setLoading(false);
    }
  }, [slug, isClientView]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const confirmedCount = participants.filter((p) => p.status === 'confirmed').length;
  const flatGames = flattenBadmintonGames(sessions);

  return {
    event,
    sessions,
    standings,
    topMmr,
    participants,
    canEdit,
    loading,
    errorMessage,
    confirmedCount,
    flatGames,
    loadData,
  };
}
